import { useEffect, useMemo, useState } from "react";

export default function AppPagination({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  totalItems = 0,
  pageSize = 10,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50],
  className = "",
}) {
  const [goToValue, setGoToValue] = useState(String(currentPage));

  useEffect(() => {
    setGoToValue(String(currentPage));
  }, [currentPage]);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
    const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    return Array.from(pages).filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
  }, [currentPage, totalPages]);

  const start = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  const handleGoToSubmit = () => {
    const parsed = Number.parseInt(goToValue, 10);
    if (Number.isNaN(parsed)) return;
    const safePage = Math.min(totalPages, Math.max(1, parsed));
    onPageChange(safePage);
    setGoToValue(String(safePage));
  };

  return (
    <div
      className={`mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-2 text-sm ${className}`}
    >
      {onPageSizeChange ? (
        <div className="flex items-center gap-2">
          <span className="text-text-500">Per page</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-text-700 outline-none focus:ring-2 focus:ring-brand-600/30"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div />
      )}

      <div className="mx-auto flex items-center gap-2">
        <span className="text-text-700">Go to</span>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={goToValue}
          onChange={(e) => setGoToValue(e.target.value)}
          onBlur={handleGoToSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleGoToSubmit();
          }}
          className="w-14 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-center font-medium text-text-700 outline-none focus:ring-2 focus:ring-brand-600/30"
        />
        <span className="text-text-700">page</span>
      </div>

      <div className="ml-auto flex items-center">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="rounded-l-xl rounded-r-none border border-slate-300 bg-slate-100 px-4 py-2 font-medium text-text-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Previous
        </button>

        {pageNumbers.map((page) => (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            className={`border border-slate-300 px-4 py-2 font-medium transition ${
              page === currentPage
                ? "bg-slate-200 text-brand-900"
                : "bg-slate-100 text-text-700 hover:bg-slate-200"
            }`}
          >
            {page}
          </button>
        ))}

        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="rounded-l-none rounded-r-xl border border-slate-300 bg-slate-100 px-4 py-2 font-medium text-text-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>

      <p className="w-full text-xs text-text-500">
        Showing <span className="font-semibold text-text-700">{start}</span> to{" "}
        <span className="font-semibold text-text-700">{end}</span> of{" "}
        <span className="font-semibold text-text-700">{totalItems}</span> records
      </p>
    </div>
  );
}
